import isVideoFile from './isVideoFile';
import isImageFile from './isImageFile';

export const normalizeMediaList = (item) => {
  if (!item) return [];
  const buildEntry = (entry) => {
    if (!entry) return null;
    if (typeof entry === 'string') {
      return { url: entry, type: isVideoFile(entry) ? 'video' : 'image' };
    }
    const url =
      entry.url ||
      entry.secure_url ||
      entry.secureUrl ||
      entry.preview ||
      entry.src ||
      entry.mediaUrl ||
      null;
    if (!url) return null;
    const type = entry.type || (isVideoFile(url) ? 'video' : 'image');
    return { url, type };
  };

  if (Array.isArray(item.media) && item.media.length) {
    return item.media.map(buildEntry).filter(Boolean);
  }
  if (Array.isArray(item.proofMedia) && item.proofMedia.length) {
    return item.proofMedia.map(buildEntry).filter(Boolean);
  }
  if (Array.isArray(item.images) && item.images.length) {
    return item.images.map(buildEntry).filter(Boolean);
  }
  if (item.img) {
    return [
      { url: item.img, type: isVideoFile(item.img) ? 'video' : 'image' },
    ];
  }
  if (item.coverMedia) {
    return [buildEntry(item.coverMedia)].filter(Boolean);
  }
  return [];
};

export const buildNormalizedUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  if (!url.includes('/upload/')) return url;

  const UP = '/upload/';
  const [left, rightRaw] = url.split(UP);
  const right = rightRaw ? rightRaw.replace(/\/+$/, '') : '';
  let tailParts = right.split('/').filter(Boolean);

  // Repair malformed Cloudinary URLs where path segments were comma-joined.
  // Example: /upload/q_auto,fl_progressive:steep,v123,Images,file.jpg
  let forcedTxSegs = null;
  if (tailParts.length === 1 && /,v\d+/.test(tailParts[0])) {
    const commaParts = tailParts[0]
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const vIndex = commaParts.findIndex((p) => /^v\d+$/.test(p));
    if (vIndex !== -1) {
      forcedTxSegs = commaParts.slice(0, vIndex);
      tailParts = commaParts.slice(vIndex);
    }
  }

  const txSegs = forcedTxSegs ? [...forcedTxSegs] : [];
  if (!forcedTxSegs) {
    while (tailParts.length && !/^v\\d+/.test(tailParts[0])) {
      txSegs.push(tailParts.shift());
    }
  }

  let tx = txSegs
    .join(',')
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);

  const video = isVideoFile(url);
  const image = !video && isImageFile(url);

  tx = Array.from(new Set(tx));

  if (video) {
    tx = tx.filter((t) => !t.startsWith('fl_progressive'));
    const required = ['f_auto', 'q_auto', 'vc_auto'];
    required.forEach((r) => {
      if (!tx.includes(r)) tx.push(r);
    });
  } else if (image) {
    if (!tx.some((t) => t.startsWith('q_auto'))) tx.unshift('q_auto');
    if (!tx.find((t) => t.startsWith('fl_progressive:steep'))) {
      tx.push('fl_progressive:steep');
    }
  }

  const finalTx = tx.join(',');
  const pathTail = tailParts.join('/');
  if (!pathTail) return url;
  if (!finalTx) return `${left}${UP}${pathTail}`;
  return `${left}${UP}${finalTx}/${pathTail}`;
};

export const derivePoster = (url) => {
  if (!url || !isVideoFile(url)) return '';
  if (!url.includes('/upload/')) return '';
  const [left, rightRaw] = url.split('/upload/');
  const right = rightRaw ? rightRaw.replace(/\/+$/, '') : '';
  const parts = right.split('/').filter(Boolean);
  const txSeg = parts[0];
  const segs = txSeg.split(',').filter(Boolean);
  if (!segs.find((s) => s.startsWith('so_'))) segs.push('so_1');
  parts[0] = Array.from(new Set(segs)).join(',');
  const last = parts[parts.length - 1];
  if (/\.(mp4|m4v|webm|ogg|ogv|mov|flv|3gp|3g2|avi|mkv)$/i.test(last)) {
    parts[parts.length - 1] = last.replace(/\.[a-z0-9]+$/i, '.jpg');
  }
  return `${left}/upload/${parts.join('/')}`;
};

const resolveCoverIndex = (item, fallbackIndex) => {
  if (!item) return fallbackIndex ?? 0;
  if (Number.isInteger(item.coverIndex)) return item.coverIndex;
  if (Number.isInteger(fallbackIndex)) return fallbackIndex;
  return 0;
};

export const getCoverMedia = (item, coverIndex) => {
  const list = normalizeMediaList(item);
  if (!list.length) return null;
  const idx = resolveCoverIndex(item, coverIndex);
  return list[idx] || list[0] || null;
};

export const getMediaThumbnail = (item, coverIndex) => {
  const cover = getCoverMedia(item, coverIndex);
  if (!cover?.url) return null;
  const normalized = buildNormalizedUrl(cover.url);
  if (cover.type === 'video') {
    return derivePoster(normalized) || normalized;
  }
  return normalized;
};
